import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { usePlan } from '../plan/hooks/plan';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import {
  SettingsSection,
  SettingsLayout,
} from '../../components/ui/settings-section';

const Usage: React.FC = () => {
  const { t } = useTranslation();
  const { plan } = useSelector((state: RootState) => state);
  const { usage, packageProperties } = plan;
  const { isPlan } = usePlan();

  const usageLimits = [
    {
      key: 'transcriptionCount',
      label: t('usage.limits.transcriptions'),
      value: usage.transcriptionCount || 0,
      max: packageProperties.transcriptionCount || 0,
    },
    {
      key: 'askAICount',
      label: t('usage.limits.aiCredits'),
      value: usage.askAICount || 0,
      max: packageProperties.askAICount || 0,
    },
    {
      key: 'videoAskAICount',
      label: t('usage.limits.videoAnalysis'),
      value: usage.videoAskAICount || 0,
      max: packageProperties.videoAskAICount || 0,
    },
  ];

  const isEnterprisePlan = isPlan(['ENTERPRISE']);
  const isBusinessPlan = isPlan(['BUSINESS']);
  const isFreePlan = isPlan(['FREE']);

  const usageLimitsShown: typeof usageLimits = [];
  if (isEnterprisePlan) {
    usageLimitsShown.push(usageLimits[2]); // videoAskAICount
  } else if (isBusinessPlan) {
    usageLimitsShown.push(usageLimits[2]); // videoAskAICount
  } else {
    usageLimitsShown.push(...usageLimits);
  }

  const getDescription = (limit: (typeof usageLimits)[0]) => {
    if (limit.max === 0) {
      return t('usage.limits.notAvailable');
    }
    const percentage = Math.round((limit.value / limit.max) * 100);
    return t('usage.limits.used', {
      used: limit.value,
      total: limit.max,
      percentage,
    });
  };

  return (
    <SettingsLayout>
      {isFreePlan && (
        <>
          <SettingsSection
            label={t('usage.freePlan.title')}
            description={t('usage.freePlan.description')}
            showSeparator={false}
          >
            <Info className="w-5 h-5 text-muted-foreground" />
          </SettingsSection>
          <Separator />
        </>
      )}

      {usageLimitsShown.map((limit, index) => (
        <SettingsSection
          key={limit.key}
          label={limit.label}
          description={getDescription(limit)}
          showSeparator={index !== usageLimitsShown.length - 1}
          className="items-center"
        >
          <Progress
            value={(limit.value / limit.max) * 100}
            className="h-2 w-48"
          />
        </SettingsSection>
      ))}
    </SettingsLayout>
  );
};

export default Usage;
